import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { space } from 'styled-system';

import { formatAsCurrency, isNumber } from '../../utils/number';
import Flex from '../Flex';
import Heading from '../Heading';
import Text from '../Text';

const primaryInfoSizeStylesMap = {
  size1: 'size2',
  size2: 'size4',
  size3: 'size5',
  size4: 'size6',
};

const primaryInfoSignalSizeStylesMap = {
  size1: 'size3',
  size2: 'size5',
  size3: 'size5',
  size4: 'size6',
};

const secondaryInfoStylesMap = {
  size1: 'size1',
  size2: 'size1',
  size3: 'size2',
  size4: 'size3',
};

const getCurrencySignal = (currency) =>
  currency.substring(0, currency.search(/\s+/));

const getCurrencyValue = (currency) =>
  currency.substring(currency.search(/\s+/) + 1, currency.length);

const StyledPriceGroup = styled('div')`
  ${css`
    display: flex;
    flex-direction: column;
    width: min-content;
  `}
  ${space}
`;

const StyledSecondaryInfo = styled(Text)`
  ${({ theme, secondaryInfo }) =>
    secondaryInfo &&
    css`
      color: ${theme.colors.price.foregroundSecondaryInfo};
      text-decoration: ${isNumber(secondaryInfo) && 'line-through'};
    `}
`;

const StyledPrimaryInfoCurrencySignal = styled(Heading)`
  ${({ theme, finished }) =>
    css`
      color: ${finished
        ? theme.colors.price.foregroundGrayscaledInfo
        : theme.colors.price.foregroundPrimaryInfo};
      padding-right: ${theme.space[1]};
    `}
`;

const StyledPrimaryInfoMain = styled(Heading)`
  ${({ theme, finished }) =>
    css`
      color: ${finished
        ? theme.colors.price.foregroundGrayscaledInfo
        : theme.colors.price.foregroundPrimaryInfo};
    `}
`;

function Price({
  currency,
  finished,
  locale,
  primaryInfo,
  secondaryInfo,
  size,
  ...rest
}) {
  const primaryInfoSize = primaryInfoSizeStylesMap[size];
  const primaryInfoSignalSize = primaryInfoSignalSizeStylesMap[size];
  const secondaryInfoSize = secondaryInfoStylesMap[size];
  const [currPrimaryInfo, currSecondaryInfo] = [primaryInfo, secondaryInfo].map(
    (info) => {
      return isNumber(info)
        ? formatAsCurrency({ number: info, currency, locale })
        : info;
    }
  );

  return (
    <StyledPriceGroup {...rest}>
      {currSecondaryInfo && (
        <StyledSecondaryInfo
          secondaryInfo={secondaryInfo}
          as="span"
          whiteSpace="nowrap"
          size={secondaryInfoSize}
        >
          {currSecondaryInfo}
        </StyledSecondaryInfo>
      )}
      {isNumber(primaryInfo) ? (
        <Flex alignItems="center">
          <StyledPrimaryInfoCurrencySignal
            size={primaryInfoSignalSize}
            as="span"
            finished={finished}
          >
            {getCurrencySignal(currPrimaryInfo)}
          </StyledPrimaryInfoCurrencySignal>
          <StyledPrimaryInfoMain
            size={primaryInfoSize}
            as="span"
            whiteSpace="nowrap"
            finished={finished}
          >
            {getCurrencyValue(currPrimaryInfo)}
          </StyledPrimaryInfoMain>
        </Flex>
      ) : (
        <StyledPrimaryInfoMain
          size={primaryInfoSize}
          as="span"
          whiteSpace="nowrap"
          finished={finished}
        >
          {currPrimaryInfo}
        </StyledPrimaryInfoMain>
      )}
    </StyledPriceGroup>
  );
}

Price.SIZES = ['size1', 'size2', 'size3'];

Price.defaultProps = {
  currency: 'BRL',
  finished: false,
  locale: 'pt-BR',
  secondaryInfo: undefined,
  size: 'size1',
  onClick: () => {},
};

Price.propTypes = {
  /**
   * Sets primary info. If a number is passed it will be formatted
   * using `currency` and `locale` props, otherwise it is rendered
   * normally as a string
   */
  primaryInfo: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  /**
   * Sets secondary info. If a number is passed it will be formatted
   * using `currency` and `locale` props, otherwise it is rendered
   * normally as a string
   */
  secondaryInfo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Sets primary info color to gray
   */
  finished: PropTypes.bool,
  /**
   * Renders a text with a determined size
   */
  size: PropTypes.oneOf(['size1', 'size2', 'size3']),
  /**
   * Currency used to format `primaryInfo` and `secondaryInfo` props.
   * A Intl.NumberFormat constructor option `currency`
   * See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat
   */
  currency: PropTypes.string,
  /**
   * Locale used to format `primaryInfo` and `secondaryInfo` props.
   * A string with a BCP 47 language tag
   * See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#Locale_identification_and_negotiation
   */
  locale: PropTypes.string,
  /**
   * Callback function on click
   */
  onClick: PropTypes.func,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
};
export default Price;
